import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import PageHeader from '../../components/ui/PageHeader';
import EmptyState from '../../components/ui/EmptyState';

const statusConfig = {
  draft:    { label: 'Borrador',  bg: '#F3F4F6', color: '#374151' },
  sent:     { label: 'Enviado',   bg: '#DBEAFE', color: '#1D4ED8' },
  accepted: { label: 'Aceptado', bg: '#D1FAE5', color: '#065F46' },
};

const Badge = ({ config }) => (
  <span style={{ display: 'inline-flex', padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600, backgroundColor: config?.bg || '#F3F4F6', color: config?.color || '#374151', whiteSpace: 'nowrap' }}>
    {config?.label || '—'}
  </span>
);

const AccountsReceivable = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('all');

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/accounts-receivable/summary');
      setData(res.data);
    } catch {
      toast.error('Error al cargar cuentas por cobrar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const fmt = (amount, currency = 'mxn', rate = 1) =>
    currency === 'usd'
      ? `$${(amount / (rate || 1)).toLocaleString('en-US', { minimumFractionDigits: 2 })} USD`
      : `$${Number(amount).toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`;

  const fmtMXN = (amount) =>
    `$${Number(amount).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

  const items   = data?.items ?? [];
  const visible = filter === 'overdue' ? items.filter(i => i.is_overdue) : items;
  const s       = data?.summary ?? {};

  const summaryCards = [
    {
      label: 'Saldo Total Pendiente',
      value: fmtMXN(s.total_pending || 0),
      sub: `${s.count_pending || 0} presupuesto${s.count_pending !== 1 ? 's' : ''} con saldo`,
      iconBg: '#FEF3C7', iconColor: '#F59E0B', border: '#FDE68A',
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />,
    },
    {
      label: 'Saldo Vencido (+30 días)',
      value: fmtMXN(s.total_overdue || 0),
      sub: `${s.count_overdue || 0} presupuesto${s.count_overdue !== 1 ? 's' : ''} vencido${s.count_overdue !== 1 ? 's' : ''}`,
      iconBg: '#FEE2E2', iconColor: '#EF4444', border: '#FECACA',
      highlight: (s.count_overdue || 0) > 0,
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />,
    },
    {
      label: 'Total Cobrado (validado)',
      value: fmtMXN(s.total_paid || 0),
      sub: 'Pagos validados por el notario',
      iconBg: '#D1FAE5', iconColor: '#10B981', border: '#A7F3D0',
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />,
    },
  ];

  return (
    <div>
      <PageHeader
        title="Cuentas por Cobrar"
        subtitle="Resumen de saldos pendientes y vencidos"
        actions={
          <button onClick={fetchData}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F9FAFB'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}>
            <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Actualizar
          </button>
        }
      />

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', flexDirection: 'column', gap: 14 }}>
          <div style={{ width: 32, height: 32, border: '3px solid #EEF0FB', borderTopColor: '#4F63D2', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
          <span style={{ fontSize: 13, color: '#9CA3AF' }}>Cargando cuentas...</span>
        </div>
      ) : (
        <>
          {/* Stat cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 20 }}>
            {summaryCards.map((card, i) => (
              <div key={i} style={{
                background: '#fff', borderRadius: 12,
                border: `1.5px solid ${card.highlight ? card.border : '#E5E7EB'}`,
                boxShadow: card.highlight ? `0 4px 14px ${card.iconColor}22` : '0 1px 4px rgba(0,0,0,0.06)',
                padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 16,
              }}>
                <div style={{ width: 48, height: 48, borderRadius: 12, backgroundColor: card.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg style={{ width: 22, height: 22, color: card.iconColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {card.icon}
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: card.highlight ? card.iconColor : '#111827', letterSpacing: '-0.5px', lineHeight: 1 }}>
                    {card.value}
                  </div>
                  <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4, fontWeight: 500 }}>{card.label}</div>
                  <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{card.sub}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Filtros */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            {[
              { key: 'all', label: `Todos (${items.length})`, danger: false },
              { key: 'overdue', label: `⚠ Vencidos (${s.count_overdue || 0})`, danger: true },
            ].map(btn => {
              const active = filter === btn.key;
              return (
                <button key={btn.key} onClick={() => setFilter(btn.key)}
                  style={{
                    padding: '7px 14px', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer',
                    border: `1.5px solid ${active ? (btn.danger ? '#EF4444' : '#4F63D2') : '#E5E7EB'}`,
                    backgroundColor: active ? (btn.danger ? '#EF4444' : '#4F63D2') : '#fff',
                    color: active ? '#fff' : btn.danger ? '#EF4444' : '#374151',
                    transition: 'all 0.15s',
                  }}>
                  {btn.label}
                </button>
              );
            })}
          </div>

          {/* Tabla */}
          <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            {visible.length === 0 ? (
              <EmptyState
                icon={filter === 'overdue' ? '✅' : '💚'}
                title={filter === 'overdue' ? 'No hay presupuestos vencidos' : 'No hay saldos pendientes'}
                subtitle={filter === 'overdue' ? 'Todos los saldos están al día' : 'Todos los presupuestos han sido pagados'}
              />
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F9FAFB' }}>
                    {['Folio', 'Cliente / Expediente', 'Tipo documento', 'Responsable', 'Total', 'Pagado', 'Saldo', 'Días', 'Estado', ''].map((h, i) => (
                      <th key={i} className={[2, 3, 7].includes(i) ? 'mobile-hide' : ''}
                        style={{ padding: '10px 14px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9CA3AF', textAlign: 'left', borderBottom: '1px solid #F3F4F6', whiteSpace: 'nowrap' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visible.map(item => (
                    <tr key={item.id}
                      style={{ backgroundColor: item.is_overdue ? '#FFFBF0' : '#fff' }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = item.is_overdue ? '#FFF8EB' : '#FAFBFF'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = item.is_overdue ? '#FFFBF0' : '#fff'}>

                      {/* Folio */}
                      <td style={{ padding: '11px 14px' }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{item.folio}</span>
                      </td>

                      {/* Cliente */}
                      <td style={{ padding: '11px 14px' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>
                          {item.client_name}
                        </div>
                        {item.case_file && (
                          <div style={{ fontSize: 11, color: '#9CA3AF', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180, marginTop: 1 }}>
                            {item.case_file.title}
                          </div>
                        )}
                      </td>

                      {/* Tipo documento */}
                      <td className="mobile-hide" style={{ padding: '11px 14px', fontSize: 12, color: '#6B7280', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.document_type}
                      </td>

                      {/* Responsable */}
                      <td className="mobile-hide" style={{ padding: '11px 14px', fontSize: 12, color: '#6B7280' }}>
                        {item.responsible || <span style={{ color: '#D1D5DB' }}>—</span>}
                      </td>

                      {/* Total */}
                      <td style={{ padding: '11px 14px' }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
                          {fmt(item.grand_total, item.currency, item.exchange_rate)}
                        </span>
                      </td>

                      {/* Pagado */}
                      <td style={{ padding: '11px 14px' }}>
                        {item.total_paid > 0
                          ? <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, backgroundColor: '#D1FAE5', color: '#065F46' }}>{fmtMXN(item.total_paid)}</span>
                          : <span style={{ color: '#D1D5DB' }}>—</span>}
                      </td>

                      {/* Saldo */}
                      <td style={{ padding: '11px 14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: item.is_overdue ? '#EF4444' : '#F59E0B' }}>
                            {fmtMXN(item.balance)}
                          </span>
                          {item.is_overdue && (
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 20, backgroundColor: '#FEE2E2', color: '#991B1B' }}>
                              vencido
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Días */}
                      <td className="mobile-hide" style={{ padding: '11px 14px' }}>
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 20,
                          backgroundColor: item.days_open > 30 ? '#FEE2E2' : item.days_open > 15 ? '#FEF3C7' : '#F3F4F6',
                          color: item.days_open > 30 ? '#991B1B' : item.days_open > 15 ? '#92400E' : '#374151',
                        }}>
                          {item.days_open}d
                        </span>
                      </td>

                      {/* Estado */}
                      <td style={{ padding: '11px 14px' }}>
                        <Badge config={statusConfig[item.status]} />
                      </td>

                      {/* Acción */}
                      <td style={{ padding: '11px 14px' }}>
                        <Link to={`/budgets/${item.id}`}
                          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 7, backgroundColor: '#EEF0FB', color: '#4F63D2', textDecoration: 'none' }}
                          title="Ver presupuesto">
                          <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AccountsReceivable;
